import { ComponentPropsWithoutRef, forwardRef, ReactNode } from 'react';
import styled from 'styled-components';
import { theme as iconTheme } from '../icon/icon.theme';
import { IconButton } from '../icon_button';
import { ChipContext, ChipSize } from './chip.context';
import { theme } from './chip.theme';

type ChipVariant = 'filled' | 'outlined';
type ChipType = 'default' | 'success' | 'info' | 'warning' | 'danger' | 'brandDark' | 'brandLight' | 'neutralLight' | 'neutralDark';

type ChipBaseProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
	/**
	 * @default medium
	 */
	size?: ChipSize;

	/**
	 * Allowed components: `ChipColorSwatch`/`ChipIcon`
	 * and `ChipLabel`.
	 */
	children: ReactNode;
};

type WithoutRemove = {
	onRemove?: never;
	removeLabel?: never;
};

type WithRemove = {
	/**
	 * Called when the user removes the chip. Providing it renders a remove
	 * button at the end of the chip. Only available on the `outlined` variant.
	 */
	onRemove: () => void;

	/**
	 * A description of what the remove button does, e.g. `'Remove Video Call'`.
	 */
	removeLabel: string;
};

type ChipOutlinedProps = ChipBaseProps &
	(WithoutRemove | WithRemove) & {
		variant: 'outlined';

		/**
		 * The outlined variant only comes in the default type.
		 * @default default
		 */
		type?: 'default';
	};

type ChipFilledProps = ChipBaseProps &
	WithoutRemove & {
		variant: 'filled';
		type: ChipType;
	};

type ChipProps = ChipOutlinedProps | ChipFilledProps;

const RemoveButton = styled(IconButton)`
	flex-shrink: 0;
`;

const StyledChip = styled.div`
	--background: transparent;
	--textColor: inherit;
	--iconColor: currentColor;
	--height: auto;
	--paddingInlineEnd: ${theme.getValue('basePaddingInline')};

	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	gap: ${theme.getValue('baseGap')};
	max-width: 100%;
	height: var(--height);
	padding-inline: ${theme.getValue('basePaddingInline')} var(--paddingInlineEnd);
	border: none;
	border-radius: ${theme.getValue('baseBorderRadius')};
	background: var(--background);
	color: var(--textColor);
	user-select: none;

	// The remove button is a circle that reaches slightly outside the rounded
	// corners of the chip, so it gets clipped. The clipping has to give way
	// while it's focused, or its focus ring gets cut off as well.
	&:not(:has(:focus-visible)) {
		overflow: hidden;
	}

	// Variants
	&[data-variant='outlined'] {
		--textColor: ${theme.getValue('outlinedTextColor')};
		--iconColor: ${theme.getValue('outlinedIconColor')};

		border: 1px solid ${theme.getValue('outlinedBorderColor')};

		// The remove button is muted rather than picking up the label color.
		& > ${RemoveButton} {
			--text-color: ${theme.getValue('outlinedRemoveIconColor')};
		}
	}

	// Types, only the outlined variant is limited to the default type.
	&[data-variant='filled'] {
		&[data-type='default'] {
			--background: ${theme.getValue('defaultBackground')};
			--textColor: ${theme.getValue('defaultTextColor')};
		}
		&[data-type='success'] {
			--background: ${theme.getValue('successBackground')};
			--textColor: ${theme.getValue('successTextColor')};
		}
		&[data-type='info'] {
			--background: ${theme.getValue('infoBackground')};
			--textColor: ${theme.getValue('infoTextColor')};
		}
		&[data-type='warning'] {
			--background: ${theme.getValue('warningBackground')};
			--textColor: ${theme.getValue('warningTextColor')};
		}
		&[data-type='danger'] {
			--background: ${theme.getValue('dangerBackground')};
			--textColor: ${theme.getValue('dangerTextColor')};
		}
		&[data-type='brandDark'] {
			--background: ${theme.getValue('brandDarkBackground')};
			--textColor: ${theme.getValue('brandDarkTextColor')};
		}
		&[data-type='brandLight'] {
			--background: ${theme.getValue('brandLightBackground')};
			--textColor: ${theme.getValue('brandLightTextColor')};
		}
		&[data-type='neutralLight'] {
			--background: ${theme.getValue('neutralLightBackground')};
			--textColor: ${theme.getValue('neutralLightTextColor')};
		}
		&[data-type='neutralDark'] {
			--background: ${theme.getValue('neutralDarkBackground')};
			--textColor: ${theme.getValue('neutralDarkTextColor')};
		}
	}

	// Sizes
	&[data-size='small'] {
		--height: ${theme.getValue('smallHeight')};
	}
	&[data-size='medium'] {
		--height: ${theme.getValue('mediumHeight')};
	}

	// The remove button brings its own padding, so the chip gives away some of
	// its own to keep the icon at the distance from the edge that the design
	// specifies.
	&[data-removable='true'] {
		--paddingInlineEnd: ${theme.getValue('baseRemovePaddingInlineEnd')};
	}

	& > ${RemoveButton} {
		// The remove button sits closer to the label than the other children do
		// to each other.
		margin-inline-start: calc(${theme.getValue('baseRemoveGap')} - ${theme.getValue('baseGap')});
	}
	&[data-size='small'] > ${RemoveButton} {
		--iconSize: ${iconTheme.getValue('xSmallSize')};
	}
	&[data-size='medium'] > ${RemoveButton} {
		--iconSize: ${iconTheme.getValue('smallSize')};
	}
`;

/**
 * A compact element for representing an attribute, a tag or a status.
 *
 * @example
 * ```tsx
 * <Chip variant='filled' type='success'>
 *   <ChipIcon type='video' />
 *   <ChipLabel>Video call</ChipLabel>
 * </Chip>
 * ```
 */
const CellulaChip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
	const { variant, type = 'default', size = 'medium', children, onRemove, removeLabel, ...rest } = props;
	const removableProps = onRemove ? { onClick: onRemove, text: removeLabel } : null;
	const isRemovable = removableProps !== null;

	return (
		<ChipContext.Provider value={{ size }}>
			<StyledChip data-variant={variant} data-type={type} data-size={size} data-removable={isRemovable} {...rest} ref={ref}>
				{children}
				{isRemovable && <RemoveButton icon='x' variant='neutral' size='small' {...removableProps} />}
			</StyledChip>
		</ChipContext.Provider>
	);
});

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

export type { ChipProps, ChipType, ChipVariant };
export { CellulaChip as Chip };
